import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

// Add missing cn utility function
const cn = (...classes) => {
  return classes?.filter(Boolean)?.join(' ');
};

const PersonalInformation = ({ user, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company: user?.company || '',
    jobTitle: user?.jobTitle || '',
    location: user?.location || '',
    bio: user?.bio || ''
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'Vorname ist erforderlich';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Nachname ist erforderlich';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    if (formData?.phone && !/^[\+]?[1-9][\d]{0,15}$/?.test(formData?.phone?.replace(/[\s\-\(\)]/g, ''))) {
      newErrors.phone = 'Bitte geben Sie eine gültige Telefonnummer ein';
    }

    if (formData?.bio && formData?.bio?.length > 500) {
      newErrors.bio = 'Bio darf maximal 500 Zeichen lang sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      onUpdate?.(formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      // Handle error (show toast, etc.)
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      company: user?.company || '',
      jobTitle: user?.jobTitle || '',
      location: user?.location || '',
      bio: user?.bio || ''
    });
    setErrors({});
    setIsEditing(false);
  };

  const formFields = [
    {
      key: 'firstName',
      label: 'Vorname',
      type: 'text',
      required: true,
      icon: 'User',
      colSpan: 'md:col-span-1'
    },
    {
      key: 'lastName',
      label: 'Nachname',
      type: 'text',
      required: true,
      icon: 'User',
      colSpan: 'md:col-span-1'
    },
    {
      key: 'email',
      label: 'E-Mail-Adresse',
      type: 'email',
      required: true,
      icon: 'Mail',
      colSpan: 'md:col-span-2'
    },
    {
      key: 'phone',
      label: 'Telefonnummer',
      type: 'tel',
      required: false,
      icon: 'Phone',
      colSpan: 'md:col-span-1'
    },
    {
      key: 'location',
      label: 'Standort',
      type: 'text',
      required: false,
      icon: 'MapPin',
      colSpan: 'md:col-span-1'
    },
    {
      key: 'company',
      label: 'Unternehmen',
      type: 'text',
      required: false,
      icon: 'Building',
      colSpan: 'md:col-span-1'
    },
    {
      key: 'jobTitle',
      label: 'Position',
      type: 'text',
      required: false,
      icon: 'Briefcase',
      colSpan: 'md:col-span-1'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Persönliche Informationen</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Verwalten Sie Ihre grundlegenden Profilinformationen
          </p>
        </div>
        
        {!isEditing ? (
          <Button
            onClick={() => setIsEditing(true)}
            iconName="Edit"
            iconPosition="left"
            size="sm"
          >
            Bearbeiten
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              onClick={handleCancel}
              variant="outline"
              size="sm"
              disabled={isSaving}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSave}
              loading={isSaving}
              iconName="Check"
              iconPosition="left"
              size="sm"
            >
              Speichern
            </Button>
          </div>
        )}
      </div>

      {/* Form */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {formFields?.map((field) => (
            <div key={field?.key} className={field?.colSpan}>
              <Input
                label={field?.label}
                type={field?.type}
                required={field?.required}
                value={formData?.[field?.key]}
                onChange={(e) => handleInputChange(field?.key, e?.target?.value)}
                error={errors?.[field?.key]}
                disabled={!isEditing}
                placeholder={isEditing ? `${field?.label} eingeben...` : ''}
                className={cn(
                  !isEditing && "bg-muted/50 cursor-not-allowed"
                )}
              />
            </div>
          ))}

          {/* Bio Field - Full Width */}
          <div className="md:col-span-2">
            <label className="text-sm font-medium leading-none text-foreground mb-2 block">
              Bio
            </label>
            <textarea
              value={formData?.bio}
              onChange={(e) => handleInputChange('bio', e?.target?.value)}
              disabled={!isEditing}
              placeholder={isEditing ? "Erzählen Sie etwas über sich..." : ''}
              rows={4}
              maxLength={500}
              className={`
                flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background 
                placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 
                focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed 
                disabled:opacity-50 resize-none
                ${!isEditing ? 'bg-muted/50 cursor-not-allowed' : ''}
                ${errors?.bio ? 'border-destructive focus-visible:ring-destructive' : ''}
              `}
            />
            <div className="flex items-center justify-between mt-1">
              {errors?.bio ? (
                <p className="text-sm text-destructive">{errors?.bio}</p>
              ) : (
                <div />
              )}
              <p className="text-xs text-muted-foreground">
                {formData?.bio?.length || 0}/500
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Account Info Card */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Konto-Informationen</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Benutzer-ID</label>
            <div className="flex items-center gap-2">
              <code className="bg-muted px-2 py-1 rounded text-sm font-mono">{user?.id}</code>
              <Button
                variant="ghost"
                size="xs"
                iconName="Copy"
                onClick={() => navigator?.clipboard?.writeText(user?.id)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Konto-Status</label>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 px-2 py-1 bg-success/10 text-success rounded">
                <Icon name="CheckCircle" size={14} />
                <span className="text-sm font-medium">Aktiv</span>
              </div>
              {user?.emailVerified && (
                <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded">
                  <Icon name="Mail" size={14} />
                  <span className="text-sm font-medium">E-Mail verifiziert</span>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Mitglied seit</label>
            <p className="text-sm text-foreground">
              {new Date(user?.joinedDate)?.toLocaleDateString('de-DE', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Letzte Anmeldung</label>
            <p className="text-sm text-foreground">
              {new Date(user?.lastLogin)?.toLocaleDateString('de-DE', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalInformation;